import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useMutation, useQuery } from '@apollo/client';
import { Card, Grid, Icon, Tooltip, Alert, Menu, MenuItem, Select, Collapse, CircularProgress, Popover, Checkbox } from '@mui/material';
import Loading from 'components/Carteles/Loading';
import SinDatos from 'components/Carteles/SinDatos';
import Footer from 'components/Footer';
import DashboardLayout from 'components/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/Navbars/DashboardNavbar';
import SoftBadge from 'components/SoftBadge';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftTypography from 'components/SoftTypography';
import DataTableCustom from 'components/Tables/DataTableCustom';
import React, { useMemo, useState, useEffect, useContext, useCallback } from 'react'
import { getUproColor } from 'utils/colors';
import dayjs from 'dayjs';
import { MessageManager } from 'context';
import PagoProveedorSubscriptions from 'components/Subscriptions/PagoProveedorSubscriptions';
import ModalOrdenPago from './components/ModalOrdenPago';
import { MenuContext } from 'context/menuContext';
import { ConfirmarContext } from 'context/ConfirmarContext';
import { ESTADOS } from './config';
import DinamicModal from 'components/Modals/DinamicModal';
import CalendarioPagos from './components/CalendarioPagos';

// Los estados de pagosProveedor ahora se envían directamente al backend
// usando el filtro 'estadoPagoProveedor'
const ALL_ESTADOS = Object.keys(ESTADOS);

const sameStates = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
};

const GET_PROVISIONES = gql`
  query getProvisiones($filter: JSON, $orderFromToday: Boolean, $pagination: PaginationInput) {
    paginationInfo {
      pages
      total
    }
    provisiones(filter: $filter, orderFromToday: $orderFromToday, pagination: $pagination) {
      id
      total
      fechaVencimiento
      anulada
      motivoAnulacion
      enviado
      fechaEnvio
      motivoProrroga
      estado
      pedidoCompra {
        id
        nombre
        tipoCompra
        tipoDetalle
        numeroExpediente
        objetoCompra
        pliegoRequiereDemanda
        __typename
      }
      proveedor {
        id
        tipoProveedor
        razonSocial
        nombre
        apellido
        cbu
        alias
        banco
        titular
        __typename
      }
      detalles {
        id
        cantidad
        cantidadEntregada
        pedidoCompraDetalle {
          requierePack
          unidadPack
          equivalenciaPack {
            materiaPrima
            packs
            __typename
          }
          __typename
        }
        materiaPrima {
          id
          tipo
          categoria
          nombre
          precio
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
            __typename
          }
          __typename
        }
        materiaPrimaRecepcion {
          id
          tipo
          categoria
          nombre
          precio
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
            __typename
          }
          __typename
        }
        precioUnitario
        subtotal
        __typename
      }
      historial {
        fecha
        evento
        motivo
        user {
          id
          nombre
          apellido
          __typename
        }
        __typename
      }
      ingresos {
        id
        tipo
        fechaEntrega
        importeBase
        movimientoDetalles {
          id
          cantidad
          precio
          materiaPrima {
            nombre
            categoria
            codigo
            tipoUnidad {
              multiplicador
              __typename
            }
            __typename
          }
          idProvisionDetalle
          __typename
        }
        pagosProveedor {
          id
          filePathFactura
          filePathComprobante
          estado
          pagado
          fechaEstimadaPago
          fechaPago
          formaPago
          cuenta
          chequeNro
          observacion
          numeroFactura
          monto
          montoBase
          impuestosTotal
          montoTotal
          impuestos {
            alicuotaIVA
            montoBase
            montoIVA
            montoIVA105
            montoIVA21
            montoPercepcionIIBB
            montoPercepcionIVA
            montoOtrosImpuestos
            __typename
          }
          motivoRechazo
          __typename
        }
        __typename
      }
      pagos {
        id
        tramite
        estado
        fechaFactura
        tipoFactura
        numeroFactura
        fechaPago
        importe
        retencio_iibb
        retencion_ganancias
        total
        __typename
      }
      solicitudesEntregaProveedor {
        id
        fechaEntrega
        horaEntregaInicio
        horaEntregaFin
        observaciones
        createdAt
        detalles {
          idProvisionDetalle
          nombre
          cantidad
          unidad
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

const GET_FILTER_OPTIONS = gql`
  query getFilterOptionsPagos {
    provisiones {
      proveedor {
        id
        tipoProveedor
        razonSocial
        nombre
        apellido
      }
    }
  }
`;

const CREATE_PAGO_PROVEEDOR = gql`
  mutation createPagoProveedor($input: PagoProveedorInput!) {
    createPagoProveedor(input: $input) {
      id
      idRemito
      filePathFactura
      filePathComprobante
      estado
      observacion
      pagado
      createdAt
      updatedAt
      remito {
        id
      }
    }
  }
`;

function Pagos() {
    const ITEMS_PER_PAGE = 12;

    const { handleSnackbar } = useContext(MessageManager);
    const [ordenPagoOpen, setOrdenPagoOpen] = useState(false);
    const [selectedRemito, setSelectedRemito] = useState(null);
    const [openMenu, setOpenMenu] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openFilters, setOpenFilters] = useState(true);
    const [filters, setFilters] = useState({
        proveedor: "",
        estado: ["Pendiente", "SinFacturar", "Aprobado"], // Array para múltiples estados por defecto
        fechaDesde: "",
        fechaHasta: "",
        idRemito: "",
        idProvision: "",
        tipoFecha: "" // "entrega" o "pago"
    });
    const { menu } = useContext(MenuContext);
    const [expandActive, setExpandActive] = useState(false);
    const [anchorEstado, setAnchorEstado] = useState(null);
    const [expandActiveTipoFecha, setExpandActiveTipoFecha] = useState(false);
    const { confirmar } = useContext(ConfirmarContext);
    const [openConfirmar, setOpenConfirmar] = useState(false);
    const [openCalendario, setOpenCalendario] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagesInfo, setPagesInfo] = useState({
        pages: 1,
        total: 0,
    });

    // Estados para filtros de fecha como en reportes
    const [fechaOption, setFechaOption] = useState(null);
    const [expandActiveFecha, setExpandActiveFecha] = useState(false);

    // Query para opciones de filtros
    const { data: optionsData, loading: optionsLoading } = useQuery(GET_FILTER_OPTIONS, {
        fetchPolicy: 'cache-first'
    });

    const searchText = useMemo(
        () => String(filters.idRemito || filters.idProvision || "").trim(),
        [filters.idRemito, filters.idProvision]
    );
    const hasSearchText = searchText.length > 0;

    const { data: dataProvisiones, loading: loadingProvisiones, refetch: refetchProvisiones } = useQuery(GET_PROVISIONES, {
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: {
                page: currentPage,
                limit: ITEMS_PER_PAGE,
            },
            filter: (() => {
                const f = {};

                if (hasSearchText) {
                    f.idRemito = searchText;
                    f.id = searchText;
                    f.estadoPagoProveedor = [...ALL_ESTADOS];
                    return f;
                }

                if (filters.idProvision && String(filters.idProvision).trim() !== "") {
                    f.id = String(filters.idProvision).trim();
                }

                // Agregar filtros que tienen valor
                if (filters.proveedor && String(filters.proveedor).trim() !== "") {
                    const proveedorId = Number(filters.proveedor);
                    if (!Number.isNaN(proveedorId)) {
                        f.idProveedor = proveedorId;
                    } else {
                        f.proveedor = String(filters.proveedor).trim();
                    }
                }

                // Filtro por estado de pagos de proveedor - manejado completamente en el backend
                if (filters.estado && Array.isArray(filters.estado) && filters.estado.length > 0) {
                    // Enviamos todos los estados al backend, incluyendo "SinFacturar"
                    const estadosValidos = filters.estado.filter(estado =>
                        estado && estado.trim() !== ""
                    );

                    if (estadosValidos.length > 0) {
                        f.estadoPagoProveedor = estadosValidos;
                    }
                }

                if (filters.idRemito && String(filters.idRemito).trim() !== "") {
                    f.idRemito = filters.idRemito.trim();
                }

                if (filters.fechaDesde && filters.fechaDesde !== "") {
                    // Formatear fecha para el backend (YYYY-MM-DD)
                    f.fechaDesde = dayjs(filters.fechaDesde).format('YYYY-MM-DD');
                }

                if (filters.fechaHasta && filters.fechaHasta !== "") {
                    // Formatear fecha para el backend (YYYY-MM-DD)
                    f.fechaHasta = dayjs(filters.fechaHasta).format('YYYY-MM-DD');
                }

                if (filters.tipoFecha && filters.tipoFecha !== "") {
                    f.tipoFecha = filters.tipoFecha;
                }

                return Object.keys(f).length > 0 ? f : null;
            })()
        }
    });

    const [updatePagoProveedor, { loading: loadingAprobarFactura }] = useMutation(CREATE_PAGO_PROVEEDOR);

    // Función para actualizar datos cuando hay cambios
    const handlePagoProveedorUpdate = (action, data) => {
        refetchProvisiones();
    };

    const getPagoProveedorFactura = (remito) => remito?.pagosProveedor;
    const getEstadoFactura = (remito) => getPagoProveedorFactura(remito)?.estado;
    const hasFacturaAdjunta = (remito) => Boolean(getPagoProveedorFactura(remito)?.filePathFactura);
    const hasComprobantePagoAdjunto = (remito) => Boolean(getPagoProveedorFactura(remito)?.filePathComprobante);

    const handleOpenMenu = (event, item) => {
        setAnchorEl(event.currentTarget);
        setOpenMenu(true);
        setSelectedRemito(item);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setOpenMenu(false);
    };

    const handleVerRemito = (remito) => {
        if (!remito?.id) {
            handleSnackbar("No se encontro el remito para visualizar.", "error");
            return;
        }

        if (typeof window === "undefined") {
            return;
        }

        window.open(`/remitoIndividual/pdf/${remito.id}`, "_blank", "noopener,noreferrer");
    };


    const handleVerOrdenPago = (remito) => {
        if (!remito?.id) {
            handleSnackbar("No se encontro el remito para visualizar.", "error");
            return;
        }

        if (typeof window === "undefined") {
            return;
        }

        window.open(`/orden-pago/pdf/${remito.id}`, "_blank", "noopener,noreferrer");
    };

    const handleVerFactura = (remito) => {
        const facturaPath = getPagoProveedorFactura(remito)?.filePathFactura;

        if (!facturaPath) {
            handleSnackbar('El remito no tiene una factura cargada.', 'warning');
            return;
        }

        if (typeof window === "undefined") {
            return;
        }

        if (facturaPath.startsWith('http')) {
            window.open(facturaPath, '_blank', 'noopener,noreferrer');
            return;
        }

        const sanitizedPath = facturaPath.startsWith('/') ? facturaPath.slice(1) : facturaPath;
        const url = resolveStoredFileUrl(sanitizedPath);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleVerComprobantePago = (remito) => {
        const comprobantePath = getPagoProveedorFactura(remito)?.filePathComprobante;

        if (!comprobantePath) {
            handleSnackbar('El remito no tiene comprobante de pago cargado.', 'warning');
            return;
        }

        if (typeof window === "undefined") {
            return;
        }

        if (comprobantePath.startsWith('http')) {
            window.open(comprobantePath, '_blank', 'noopener,noreferrer');
            return;
        }

        const sanitizedPath = comprobantePath.startsWith('/') ? comprobantePath.slice(1) : comprobantePath;
        const url = resolveStoredFileUrl(sanitizedPath);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleAdjuntarComprobantePago = (remito) => {
        if (loadingFactura) return;

        if (getEstadoFactura(remito) !== "OPFinalizada") {
            handleSnackbar('Solo se puede adjuntar comprobante cuando la OP está finalizada.', 'warning');
            return;
        }

        const alreadyHadComprobante = hasComprobantePagoAdjunto(remito);

        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
        fileInput.style.display = 'none';

        fileInput.onchange = async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;

            const resolvedFile = await new Promise((resolve) => {
                if (file.size <= 2000000) {
                    resolve(file);
                } else {
                    resolve(null);
                }
            });

            if (!resolvedFile) {
                handleSnackbar('El archivo debe tener un tamaño máximo de 2MB.', 'warning');
                return;
            }

            try {
                await updatePagoProveedor({
                    variables: {
                        input: {
                            idRemito: Number(remito.id),
                            estado: "OPFinalizada",
                            fileUploadPagado: resolvedFile,
                        },
                    },
                });

                await refetchProvisiones();
                handleSnackbar(
                    alreadyHadComprobante
                        ? 'Comprobante de pago actualizado correctamente.'
                        : 'Comprobante de pago cargado correctamente.',
                    'success'
                );
            } catch (error) {
                console.error('Error al cargar comprobante de pago:', error);
                handleSnackbar('Error al cargar el comprobante de pago. Intenta nuevamente.', 'error');
            }
        };

        document.body.appendChild(fileInput);
        fileInput.click();
        document.body.removeChild(fileInput);
    };

    const handleUpdateEstadoFactura = async (remito, nuevoEstado) => {
        if (loadingFactura) return;

        const facturaPath = getPagoProveedorFactura(remito)?.filePathFactura;

        if (!facturaPath) {
            handleSnackbar(nuevoEstado === "Aprobado" ? 'Este remito no tiene factura para aprobar.' : 'Este remito no tiene factura para rechazar.', 'warning');
            return;
        }

        confirmar({
            icon: "warning",
            title: nuevoEstado === "Rechazado" ? "Rechazar Factura" : "Aprobar Factura",
            message: `¿Está seguro que desea ${nuevoEstado === "Rechazado" ? "rechazar" : "aprobar"} la factura?`,
            pedirMotivo: nuevoEstado === "Rechazado",
            placeholder: "Ingrese el motivo del rechazo",
            func: async (motivo) => {
                try {
                    await updatePagoProveedor({
                        variables: {
                            input: {
                                idRemito: Number(remito.id),
                                estado: nuevoEstado,
                                ...nuevoEstado === "Rechazado" && { motivoRechazo: motivo }
                            },
                        },
                    });

                    await refetchProvisiones();
                    handleSnackbar(nuevoEstado === "Aprobado" ? 'Factura aprobada!' : 'Factura rechazada!', 'success');
                } catch (error) {
                    console.error(nuevoEstado === "Aprobado" ? 'Error al aprobar factura:' : 'Error al rechazar factura:', error);
                    handleSnackbar(nuevoEstado === "Aprobado" ? 'Error al aprobar la factura. Intenta nuevamente.' : 'Error al rechazar la factura. Intenta nuevamente.', 'error');
                }
            },
        });
    }

    const handlePagar = async (remito) => {
        if (loadingFactura) return;

        const facturaPath = getPagoProveedorFactura(remito)?.filePathFactura;

        if (!facturaPath) {
            handleSnackbar('Este remito no tiene factura para pagar.', 'warning');
            return;
        }

        try {
            await updatePagoProveedor({
                variables: {
                    input: {
                        idRemito: Number(remito.id),
                        estado: "OPFinalizada"
                    },
                },
            });

            await refetchProvisiones();
            handleSnackbar('Factura pagada!', 'success');
        } catch (error) {
            console.error('Error al pagar factura:', error);
            handleSnackbar('Error al pagar la factura. Intenta nuevamente.', 'error');
        }
    };

    const handleCrearOrdenPago = async (remito) => {
        if (!remito) return;
        if (loadingFactura) return;
        setSelectedRemito(remito || null);
        setOrdenPagoOpen(true);
    };

    const handleEditarOrdenPago = async (remito) => {
        if (!remito) return;
        if (loadingFactura) return;
        setSelectedRemito(remito || null);
        setOrdenPagoOpen(true);
    };

    const remitosColumns = [
        {
            field: "id",
            width: "12%",
            headerName: "N° Remito",
            sortable: true,
            align: "center",
            renderCell: (remito) => (
                <SoftBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small" sx={{ color: "text.secondary", opacity: 0.7 }}>tag</Icon>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        {remito.id || 0}
                    </SoftTypography>
                </SoftBox>
            ),
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ width: "100%", overflow: "hidden" }}
                >
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="secondary"
                        sx={{ letterSpacing: "0.05em", whiteSpace: "nowrap" }}
                    >
                        {column.headerName}
                    </SoftTypography>
                    {isCurrentSort && direction && (
                        <Icon fontSize="small" sx={{ color: "primary.main" }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    )}
                </SoftBox>
            ),
        },
        {
            field: "OP",
            width: "8%",
            headerName: "N° OP",
            align: "center",
            sortable: true,
            renderCell: (remito) => (
                <SoftBox
                    sx={{
                        backgroundColor: "grey.100",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        display: "inline-block"
                    }}
                >
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                        {remito.OP || 0}
                    </SoftTypography>
                </SoftBox>
            ),
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ width: "100%", overflow: "hidden" }}
                >
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="secondary"
                        sx={{ letterSpacing: "0.05em", whiteSpace: "nowrap" }}
                    >
                        {column.headerName}
                    </SoftTypography>
                </SoftBox>
            ),
        },
        {
            field: "proveedor",
            width: "28%",
            headerName: "Proveedor",
            align: "left",
            sortable: true,
            renderCell: (remito) => {
                const nombre = remito.proveedor?.nombre && remito.proveedor?.apellido
                    ? `${remito.proveedor.nombre} ${remito.proveedor.apellido}`
                    : remito.proveedor?.razonSocial || '-';
                return (
                    <SoftBox display="flex" flexDirection="column">
                        <SoftTypography variant="button" fontWeight="medium" color="dark">
                            {nombre}
                        </SoftTypography>
                        {remito.proveedor?.cbu && (
                            <SoftTypography variant="caption" color="text" sx={{ fontSize: "10px" }}>
                                CBU: {remito.proveedor.cbu}
                            </SoftTypography>
                        )}
                    </SoftBox>
                )
            },
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{ width: "100%", overflow: "hidden", pl: 1 }}
                >
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="secondary"
                        sx={{ letterSpacing: "0.05em", whiteSpace: "nowrap" }}
                    >
                        {column.headerName}
                    </SoftTypography>
                </SoftBox>
            ),
        },
        {
            field: "fechaEntrega",
            width: "14%",
            headerName: "Entrega",
            sortable: true,
            align: "center",
            renderCell: (remito) => (
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                    <Icon fontSize="small" sx={{ color: "info.main", opacity: 0.8 }}>local_shipping</Icon>
                    <SoftTypography variant="caption" fontWeight="medium">
                        {remito.fechaEntrega ? dayjs(remito.fechaEntrega).format('DD/MM/YYYY') : '-'}
                    </SoftTypography>
                </SoftBox>
            ),
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ width: "100%", overflow: "hidden" }}
                >
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="secondary"
                        sx={{ letterSpacing: "0.05em", whiteSpace: "nowrap" }}
                    >
                        {column.headerName}
                    </SoftTypography>
                </SoftBox>
            ),
        },
        {
            field: "estado",
            width: "18%",
            headerName: "Estado Factura",
            align: "center",
            sortable: true,
            customSortValue: (remito) => {
                const estado = remito?.pagosProveedor?.estado;
                return estado || "SinFacturar";
            },
            renderCell: (remito) => {
                const pagos = remito?.pagosProveedor;
                const estado = pagos?.estado;

                const badgeProps = pagos
                    ? {
                        color: ESTADOS[estado]?.color || "info",
                        badgeContent: ESTADOS[estado]?.label || "-",
                    }
                    : {
                        color: "uproPink",
                        badgeContent: "Sin facturar",
                    };

                return (
                    <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ width: "100%" }}
                    >
                        <SoftBadge
                            variant="gradient"
                            size="extra-small"
                            color={badgeProps.color}
                            badgeContent={badgeProps.badgeContent}
                            container
                        />
                    </SoftBox>
                );
            },
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ width: "100%", overflow: "hidden" }}
                >
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="secondary"
                        sx={{ letterSpacing: "0.05em", whiteSpace: "nowrap" }}
                    >
                        {column.headerName}
                    </SoftTypography>
                </SoftBox>
            ),
        },
        {
            field: "fechaPago",
            width: "14%",
            headerName: "Pago",
            align: "center",
            sortable: true,
            customSortValue: (remito) => {
                const fechaPago = remito?.pagosProveedor?.fechaPago;
                return fechaPago || "-";
            },
            renderCell: (remito) => {
                const fecha = remito?.pagosProveedor?.fechaPago || remito?.pagosProveedor?.fechaEstimadaPago;
                const isEstimada = remito?.pagosProveedor?.fechaEstimadaPago && !remito?.pagosProveedor?.fechaPago;

                return (
                    <Tooltip title={isEstimada ? 'Fecha estimada de pago' : (!fecha ? "Sin pago" : "")} placement="top">
                        <SoftBox display="flex" alignItems="center" gap={0.5}>
                            <Icon fontSize="small" sx={{ color: fecha ? (isEstimada ? "warning.main" : "success.main") : "secondary.main", opacity: 0.8 }}>
                                {fecha ? (isEstimada ? "event_repeat" : "event_available") : "event_busy"}
                            </Icon>
                            <SoftTypography variant="caption" fontWeight="bold" color={fecha ? (isEstimada ? "warning" : "dark") : "secondary"}>
                                {fecha ? dayjs(fecha).format('DD/MM/YYYY') : 'Sin pago'}
                            </SoftTypography>
                        </SoftBox>
                    </Tooltip>
                )
            },
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ width: "100%", overflow: "hidden" }}
                >
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="secondary"
                        sx={{ letterSpacing: "0.05em", whiteSpace: "nowrap" }}
                    >
                        {column.headerName}
                    </SoftTypography>
                </SoftBox>
            )
        },
        {
            field: "acciones",
            headerName: "Acciones",
            width: "6%",
            align: "center",
            renderCell: (remito) =>
            (
                <SoftBox
                    sx={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        transition: "all 0.2s",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.05)",
                            transform: "scale(1.1)"
                        }
                    }}
                    onClick={(e) => handleOpenMenu(e, remito)}
                >
                    <Icon sx={{ color: "text.secondary" }} fontSize="small">
                        more_vert
                    </Icon>
                </SoftBox>
            ),
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: "100%" }}
                >
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="secondary"
                    >
                        {column.headerName}
                    </SoftTypography>
                </SoftBox>
            )
        }
    ];

    // Acciones para los remitos
    const remitosActions = [
        {
            icon: "picture_as_pdf",
            tooltip: "Remito",
            color: "primary",
            onClick: handleVerRemito,
        },
        {
            icon: "visibility",
            tooltip: "Factura",
            color: "warning",
            onClick: handleVerFactura,
            hidden: (remito) => !hasFacturaAdjunta(remito),
        },
        {
            icon: "block",
            tooltip: "Ver motivo del rechazo de la factura",
            color: "error",
            onClick: () => {
                setOpenConfirmar(true);
            },
            hidden: (remito) => !hasFacturaAdjunta(remito) || (remito?.pagosProveedor && remito?.pagosProveedor?.estado !== "Rechazado"),
        },
        {
            icon: (remito) => (hasFacturaAdjunta(remito) ? "check_circle" : null),
            tooltip: (remito) => (hasFacturaAdjunta(remito) ? "Aprobar" : null),
            color: (remito) => (hasFacturaAdjunta(remito) ? "success" : null),
            onClick: (remito) => handleUpdateEstadoFactura(remito, "Aprobado"),
            hidden: (remito) => remito?.pagosProveedor && !["Pendiente", "SinFacturar"].includes(remito?.pagosProveedor?.estado),
        },
        {
            icon: (remito) => (hasFacturaAdjunta(remito) ? "cancel" : null),
            tooltip: (remito) => (hasFacturaAdjunta(remito) ? "Rechazar" : null),
            color: (remito) => (hasFacturaAdjunta(remito) ? "error" : null),
            onClick: (remito) => handleUpdateEstadoFactura(remito, "Rechazado"),
            hidden: (remito) => remito?.pagosProveedor && !["Pendiente", "SinFacturar"].includes(remito?.pagosProveedor?.estado),
        },
        {
            icon: "add",
            tooltip: "Crear Orden de Pago",
            color: "dark",
            onClick: handleCrearOrdenPago,
            hidden: (remito) => getEstadoFactura(remito) !== "Aprobado" || !hasFacturaAdjunta(remito),
        },
        {
            icon: "edit_note",
            tooltip: "Editar Orden de pago",
            color: "warning",
            onClick: handleEditarOrdenPago,
            hidden: (remito) =>
                !["OPEmitida", "OPFinalizada"].includes(getEstadoFactura(remito)) ||
                hasComprobantePagoAdjunto(remito) ||
                !hasFacturaAdjunta(remito),
        },
        {
            icon: "attach_money",
            tooltip: "Pagar",
            color: "success",
            onClick: handlePagar,
            hidden: (remito) => getEstadoFactura(remito) !== "OPEmitida" || !hasFacturaAdjunta(remito),
        },
        {
            icon: "paid",
            tooltip: "Orden de Pago",
            color: "primary",
            onClick: handleVerOrdenPago,
            hidden: (remito) => !["OPEmitida", "OPFinalizada"].includes(getEstadoFactura(remito)) || !hasFacturaAdjunta(remito),
        },
        {
            icon: (remito) => hasComprobantePagoAdjunto(remito) ? "drive_folder_upload" : "upload_file",
            tooltip: (remito) => hasComprobantePagoAdjunto(remito) ? "Cambiar comprobante de pago" : "Cargar comprobante de pago",
            color: (remito) => hasComprobantePagoAdjunto(remito) ? "warning" : "info",
            onClick: handleAdjuntarComprobantePago,
            hidden: (remito) => getEstadoFactura(remito) !== "OPFinalizada",
        },
        {
            icon: "payments",
            tooltip: "Ver comprobante de pago",
            color: "info",
            onClick: handleVerComprobantePago,
            hidden: (remito) => getEstadoFactura(remito) !== "OPFinalizada" || !hasComprobantePagoAdjunto(remito),
        }
    ];

    const loadingFactura = loadingAprobarFactura;

    const renderMenu = (
        <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            open={Boolean(openMenu)}
            onClose={handleCloseMenu}
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    minWidth: "220px",
                    padding: "8px",
                    backgroundColor: "#ffffff !important"
                }
            }}
        >
            <SoftBox px={2} py={1}>
                <SoftTypography variant="caption" color="secondary" fontWeight="bold" textTransform="uppercase">
                    Acciones Disponibles
                </SoftTypography>
            </SoftBox>
            {remitosActions
                .filter(action => {
                    if (typeof action.hidden === 'function') {
                        return !action.hidden(selectedRemito);
                    }
                    return true;
                })
                .map((action, index) => {
                    const iconValue = typeof action.icon === 'function' ? action.icon(selectedRemito) : action.icon;
                    const tooltipValue = typeof action.tooltip === 'function' ? action.tooltip(selectedRemito) : action.tooltip;
                    const colorValue = typeof action.color === 'function' ? action.color(selectedRemito) : action.color;

                    if (!iconValue) return null;

                    return (
                        <MenuItem
                            key={index}
                            disabled={loadingFactura}
                            onClick={() => {
                                action.onClick(selectedRemito);
                                handleCloseMenu();
                            }}
                            sx={{
                                borderRadius: "8px",
                                mb: 0.5,
                                "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.03)"
                                }
                            }}
                        >
                            <SoftBox display="flex" alignItems="center" gap={1.5} width="100%">
                                <SoftBox
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    width="28px"
                                    height="28px"
                                    borderRadius="6px"
                                    bgColor={colorValue}
                                    color="white"
                                    variant="gradient"
                                >
                                    <Icon sx={{ fontSize: '14px !important' }}>
                                        {iconValue}
                                    </Icon>
                                </SoftBox>
                                <SoftTypography variant="button" fontWeight="medium" color="dark">
                                    {tooltipValue}
                                </SoftTypography>
                            </SoftBox>
                        </MenuItem>
                    );
                })
                .filter(Boolean)
            }
        </Menu>
    );

    const provisiones = dataProvisiones?.provisiones || [];

    const proveedoresOptions = useMemo(() => {
        const proveedoresMap = new Map();

        (optionsData?.provisiones || provisiones)
            .filter((p) => p?.proveedor?.id)
            .forEach((p) => {
                if (!proveedoresMap.has(p.proveedor.id)) {
                    proveedoresMap.set(p.proveedor.id, p.proveedor);
                }
            });

        return Array.from(proveedoresMap.values()).map((proveedor) => {
            const nombre = proveedor?.razonSocial ||
                `${proveedor?.nombre || ''} ${proveedor?.apellido || ''}`.trim();
            return {
                id: proveedor.id,
                nombre,
                proveedor
            };
        });
    }, [optionsData?.provisiones, provisiones]);

    const selectedProveedor = useMemo(() => {
        if (!filters.proveedor) return null;
        return proveedoresOptions.find(p => p.id == filters.proveedor);
    }, [filters.proveedor, proveedoresOptions]);

    const getProveedorButtonText = useCallback(() => {
        if (!filters.proveedor) return "Todos los proveedores";
        if (optionsLoading) return <CircularProgress size={14} />;
        return selectedProveedor?.nombre || "Todos los proveedores";
    }, [filters.proveedor, optionsLoading, selectedProveedor]);

    // Función para obtener el texto del botón de estado
    const getEstadoButtonText = useCallback(() => {
        if (!filters.estado || !Array.isArray(filters.estado) || filters.estado.length === 0) {
            return "Todos los Estados";
        }
        if (filters.estado.length === 1) {
            return ESTADOS[filters.estado[0]]?.label || "Estado seleccionado";
        }
        return `${filters.estado.length} estados seleccionados`;
    }, [filters.estado]);

    // Función para obtener el texto del botón de tipo de fecha
    const getTipoFechaButtonText = useCallback(() => {
        const tiposDisplay = {
            "entrega": "Fecha de Entrega",
            "pago": "Fecha de Pago"
        };
        return tiposDisplay[filters.tipoFecha] || "Sin fecha seleccionada";
    }, [filters.tipoFecha]);

    // Función para obtener el texto del botón de fecha
    const getFechaButtonText = useCallback(() => {
        const opcionesDisplay = {
            "hoy": "Hoy",
            "ayer": "Ayer",
            "ultimos7": "Últimos 7 días",
            "ultimos30": "Últimos 30 días",
            "personalizado": "Rango personalizado"
        };
        return opcionesDisplay[fechaOption] || "Sin período seleccionado";
    }, [fechaOption]);

    // Función para aplicar fechas según la opción seleccionada
    const aplicarFechaOption = useCallback((opcion) => {
        const hoy = dayjs();
        let desde = "";
        let hasta = "";

        switch (opcion) {
            case "hoy":
                desde = hoy.format("YYYY-MM-DD");
                hasta = hoy.format("YYYY-MM-DD");
                break;
            case "ayer":
                desde = hoy.subtract(1, 'day').format("YYYY-MM-DD");
                hasta = hoy.subtract(1, 'day').format("YYYY-MM-DD");
                break;
            case "ultimos7":
                desde = hoy.subtract(6, 'days').format("YYYY-MM-DD");
                hasta = hoy.format("YYYY-MM-DD");
                break;
            case "ultimos30":
                desde = hoy.subtract(29, 'days').format("YYYY-MM-DD");
                hasta = hoy.format("YYYY-MM-DD");
                break;
            case "personalizado":
                // No cambiar las fechas, mantener las actuales
                return;
        }

        setFilters(prev => ({
            ...prev,
            fechaDesde: desde,
            fechaHasta: hasta
        }));
    }, []);

    const remitos = useMemo(() => {
        // Ya no necesitamos filtrado local - el backend maneja todos los filtros
        // Solo transformamos los datos de las provisiones en la estructura esperada
        return provisiones.flatMap((provision) =>
            (provision.ingresos?.filter((ingreso) => ingreso.tipo === "Remito Proveedor") || [])
                .map((ingreso) => {
                    const movimientos = ingreso.movimientoDetalles || [];
                    const provisionDetallesById = new Map((provision.detalles || []).map((detalleProvision) => [
                        Number(detalleProvision.id),
                        detalleProvision,
                    ]));

                    const totalCalculado = movimientos?.reduce((sum, detalle) => {
                        const provisionDetalle = provisionDetallesById.get(Number(detalle.idProvisionDetalle));
                        const pcd = provisionDetalle?.pedidoCompraDetalle;
                        const mp = detalle?.materiaPrima ?? provisionDetalle?.materiaPrimaRecepcion ?? provisionDetalle?.materiaPrima;

                        const multiplicador = Number(mp?.tipoUnidad?.multiplicador) || 1;
                        const equivPack = Number(pcd?.equivalenciaPack?.materiaPrima) || 1;
                        const cantidadOriginal = Number(detalle.cantidad) || 0;
                        const cantidadBase = pcd?.requierePack
                            ? (cantidadOriginal / equivPack) / multiplicador
                            : cantidadOriginal / multiplicador;

                        const precioUnit = Number(provisionDetalle?.precioUnitario) || 0;
                        const totalDetalle = cantidadBase * precioUnit;

                        return sum + totalDetalle;
                    }, 0) || 0;
                    const total = ingreso.importeBase ?? totalCalculado;

                    return {
                        ...ingreso,
                        OP: provision.id,
                        proveedor: provision.proveedor,
                        total,
                    };
                })
        );
    }, [provisiones]);

    const totalPages = useMemo(() => Math.max(1, Number(pagesInfo?.pages) || 1), [pagesInfo?.pages]);



    const toggleFilters = () => {
        if (openFilters) {
            setOpenFilters(false);
            setFilters({
                proveedor: "",
                estado: "",
                fechaDesde: "",
                fechaHasta: "",
                idRemito: "",
                tipoFecha: ""
            })
            setCurrentPage(1);
            setFechaOption(null)
        } else {
            setOpenFilters(true);
        }
    };

    const handleFilterChange = (filterName, value) => {
        const normalizedValue = value === -1 || value === "" ? "" : value;
        const newFilters = {
            ...filters,
            [filterName]: typeof normalizedValue === "number" ? String(normalizedValue) : normalizedValue
        };
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const handleSearchChange = (value) => {
        const normalizedValue = value === -1 || value === "" ? "" : value;
        const nextValue = typeof normalizedValue === "number" ? String(normalizedValue) : normalizedValue;
        setFilters((prev) => ({
            ...prev,
            idRemito: nextValue,
            idProvision: nextValue,
        }));
        setCurrentPage(1);
    };

    // Aplicar fechas cuando cambie la opción de fecha
    useEffect(() => {
        if (fechaOption !== "personalizado") {
            aplicarFechaOption(fechaOption);
        }
    }, [fechaOption, aplicarFechaOption]);

    // Inicializar sin filtros de fecha por defecto para cumplir con la llamada solicitada
    useEffect(() => {
        // Al montar, no aplicamos fecha de hoy para que coincida con la consulta solicitada
        // que solo incluye estados.
    }, []); // Solo ejecutar al montar el componente

    useEffect(() => {
        if (dataProvisiones?.paginationInfo) {
            setPagesInfo(dataProvisiones.paginationInfo);
        }
    }, [dataProvisiones?.paginationInfo]);

    useEffect(() => {
        if (!hasSearchText) return;
        const provisionesBusqueda = dataProvisiones?.provisiones || [];
        if (provisionesBusqueda.length === 0) return;

        const estadosEnResultados = new Set();
        provisionesBusqueda.forEach((provision) => {
            const ingresos = Array.isArray(provision?.ingresos) ? provision.ingresos : [];
            ingresos
                .filter((ingreso) => ingreso?.tipo === "Remito Proveedor")
                .forEach((ingreso) => {
                    const estado = ingreso?.pagosProveedor?.estado || "SinFacturar";
                    if (estado) estadosEnResultados.add(estado);
                });
        });

        const estadosAuto = ALL_ESTADOS.filter((estado) => estadosEnResultados.has(estado));
        if (estadosAuto.length === 0) return;

        if (!sameStates(filters.estado, estadosAuto)) {
            setFilters((prev) => ({
                ...prev,
                estado: estadosAuto,
            }));
        }
    }, [hasSearchText, dataProvisiones?.provisiones, filters.estado]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);


    return (
        <DashboardLayout>
            <DashboardNavbar />
            <PagoProveedorSubscriptions
                onUpdate={handlePagoProveedorUpdate}
                showNotifications={false}
            />
            <SoftBox py={3} px={1}>
                {/* Header Section */}
                <SoftBox mb={3}>
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item>
                            <SoftBox display="flex" alignItems="center" gap={1.5}>
                                <SoftBox
                                    variant="gradient"
                                    bgColor="uproGreen"
                                    color="green"
                                    borderRadius="lg"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    width="42px"
                                    height="42px"
                                    shadow="md"
                                >
                                    <Icon fontSize="medium">payments</Icon>
                                </SoftBox>
                                <SoftBox>
                                    <SoftTypography variant="h5" fontWeight="bold"> Gestión de Pagos</SoftTypography>
                                    <SoftTypography variant="caption" color="text" fontWeight="regular">
                                        Administra y monitorea el estado de facturación de tus proveedores
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        <Grid item>
                            <SoftBox display="flex" gap={1.5}>
                                <SoftButton
                                    variant={openCalendario ? "gradient" : "outlined"}
                                    color="uproGreen"
                                    onClick={() => setOpenCalendario(true)}
                                    circular
                                    sx={{ boxShadow: "none" }}
                                >
                                    <Icon sx={{ mr: 1 }}>calendar_today</Icon>
                                    Calendario de pagos
                                </SoftButton>
                                <SoftButton
                                    variant={openFilters ? "gradient" : "outlined"}
                                    color="uproYellow"
                                    onClick={toggleFilters}
                                    circular
                                    sx={{ boxShadow: "none" }}
                                >
                                    <Icon sx={{ mr: 1 }}>
                                        {openFilters ? "filter_alt_off" : "filter_alt"}
                                    </Icon>
                                    {openFilters ? "Ocultar filtros" : "Filtrar resultados"}
                                </SoftButton>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>

                {/* Filters Section grouped by Cards */}
                <Collapse in={openFilters} timeout="auto" unmountOnExit>
                    <SoftBox mb={4}>
                        <Grid container spacing={3}>
                            {/* Card 1: Información General */}
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%", border: "1px solid", borderColor: "grey.200", boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)" }}>
                                    <SoftBox p={2.5}>
                                        <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
                                            <Icon color="info" fontSize="small">info</Icon>
                                            <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase">
                                                Información General
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox display="flex" flexDirection="column" gap={2}>
                                            <SoftButton
                                                variant="outlined"
                                                color="primary"
                                                fullWidth
                                                sx={{ justifyContent: "space-between", textTransform: "none", fontWeight: "medium", borderRadius: "10px", py: 1 }}
                                                onClick={(event) => {
                                                    setExpandActive(true);
                                                    menu({
                                                        open: event.currentTarget,
                                                        options: proveedoresOptions.map((opt) => ({
                                                            name: opt.nombre,
                                                            icon: <Icon sx={{ color: "success.main" }}>person</Icon>,
                                                            onClick: () => handleFilterChange("proveedor", String(opt.id))
                                                        })),
                                                        onClose: () => setExpandActive(false),
                                                    });
                                                }}
                                            >
                                                <SoftBox display="flex" alignItems="center" gap={1}>
                                                    <Icon color="inherit">person</Icon>
                                                    {getProveedorButtonText()}
                                                </SoftBox>
                                                <Icon sx={{ rotate: expandActive ? "180deg" : "0deg", transition: "0.3s" }}>expand_more</Icon>
                                            </SoftButton>

                                            <SoftButton
                                                variant="outlined"
                                                color="primary"
                                                fullWidth
                                                sx={{ justifyContent: "space-between", textTransform: "none", fontWeight: "medium", borderRadius: "10px", py: 1 }}
                                                onClick={(event) => setAnchorEstado(event.currentTarget)}
                                            >
                                                <SoftBox display="flex" alignItems="center" gap={1}>
                                                    <Icon color="inherit">assignment</Icon>
                                                    {getEstadoButtonText()}
                                                </SoftBox>
                                                <Icon sx={{ rotate: Boolean(anchorEstado) ? "180deg" : "0deg", transition: "0.3s" }}>expand_more</Icon>
                                            </SoftButton>

                                            <Popover
                                                open={Boolean(anchorEstado)}
                                                anchorEl={anchorEstado}
                                                onClose={() => setAnchorEstado(null)}
                                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                                                PaperProps={{ sx: { p: 2, minWidth: 250, borderRadius: "12px", mt: 1, boxShadow: "0 10px 30px rgba(0,0,0,0.15)", bgcolor: "#ffffff !important", border: "1px solid rgba(0,0,0,0.05)" } }}
                                            >
                                                <SoftBox display="flex" flexDirection="column" gap={0.5}>
                                                    <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" sx={{ pb: 1, borderBottom: "1px solid", borderColor: "grey.100", mb: 1, color: "grey.600" }}>
                                                        Filtrar por Estados
                                                    </SoftTypography>

                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        p={0.5}
                                                        sx={{
                                                            cursor: "pointer",
                                                            borderRadius: "8px",
                                                            "&:hover": { bgcolor: "grey.100" }
                                                        }}
                                                        onClick={() => handleFilterChange("estado", [])}
                                                    >
                                                        <Icon color={(!filters.estado || filters.estado.length === 0) ? "primary" : "secondary"} sx={{ mr: 1.5 }}>list</Icon>
                                                        <SoftTypography variant="button" fontWeight={(!filters.estado || filters.estado.length === 0) ? "bold" : "regular"}>
                                                            Todos los Estados
                                                        </SoftTypography>
                                                    </SoftBox>

                                                    {Object.keys(ESTADOS).map((est) => {
                                                        const isSelected = Array.isArray(filters.estado) && filters.estado.includes(est);
                                                        return (
                                                            <SoftBox
                                                                key={est}
                                                                display="flex"
                                                                alignItems="center"
                                                                p={0.5}
                                                                sx={{
                                                                    cursor: "pointer",
                                                                    borderRadius: "8px",
                                                                    "&:hover": { bgcolor: "grey.100" }
                                                                }}
                                                                onClick={() => {
                                                                    const current = [...(filters.estado || [])];
                                                                    handleFilterChange("estado", isSelected ? current.filter(x => x !== est) : [...current, est]);
                                                                }}
                                                            >
                                                                <Checkbox checked={isSelected} size="small" sx={{ p: 0.5 }} />
                                                                <SoftTypography variant="button" color="text" fontWeight={isSelected ? "bold" : "regular"} sx={{ ml: 1 }}>
                                                                    {ESTADOS[est].label}
                                                                </SoftTypography>
                                                            </SoftBox>
                                                        );
                                                    })}
                                                </SoftBox>
                                            </Popover>
                                        </SoftBox>
                                    </SoftBox>
                                </Card>
                            </Grid>

                            {/* Card 2: Rango Temporal */}
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%", border: "1px solid", borderColor: "grey.200", boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)" }}>
                                    <SoftBox p={2.5}>
                                        <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
                                            <Icon color="info" fontSize="small">calendar_month</Icon>
                                            <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase">
                                                Rango Temporal
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox display="flex" flexDirection="column" gap={2}>
                                            <SoftButton
                                                variant="outlined"
                                                color="info"
                                                fullWidth
                                                sx={{ justifyContent: "space-between", textTransform: "none", borderRadius: "10px", py: 1 }}
                                                onClick={(e) => {
                                                    setExpandActiveTipoFecha(true);
                                                    menu({
                                                        open: e.currentTarget,
                                                        options: [
                                                            { name: "Fecha de Entrega", onClick: () => handleFilterChange("tipoFecha", "entrega") },
                                                            { name: "Fecha de Pago", onClick: () => handleFilterChange("tipoFecha", "pago") }
                                                        ],
                                                        onClose: () => setExpandActiveTipoFecha(false),
                                                    });
                                                }}
                                            >
                                                <SoftBox display="flex" alignItems="center" gap={1}>
                                                    <Icon color="inherit">event_repeat</Icon>
                                                    {getTipoFechaButtonText()}
                                                </SoftBox>
                                                <Icon sx={{ rotate: expandActiveTipoFecha ? "180deg" : "0deg", transition: "0.3s" }}>expand_more</Icon>
                                            </SoftButton>

                                            <SoftButton
                                                variant="outlined"
                                                color="info"
                                                fullWidth
                                                sx={{ justifyContent: "space-between", textTransform: "none", borderRadius: "10px", py: 1 }}
                                                onClick={(e) => {
                                                    setExpandActiveFecha(true);
                                                    menu({
                                                        open: e.currentTarget,
                                                        options: [
                                                            { name: "Hoy", onClick: () => setFechaOption("hoy") },
                                                            { name: "Ayer", onClick: () => setFechaOption("ayer") },
                                                            { name: "Últimos 7 días", onClick: () => setFechaOption("ultimos7") },
                                                            { name: "Últimos 30 días", onClick: () => setFechaOption("ultimos30") },
                                                            { name: "Personalizado", onClick: () => setFechaOption("personalizado") }
                                                        ],
                                                        onClose: () => setExpandActiveFecha(false),
                                                    });
                                                }}
                                            >
                                                <SoftBox display="flex" alignItems="center" gap={1}>
                                                    <Icon color="inherit">date_range</Icon>
                                                    {getFechaButtonText()}
                                                </SoftBox>
                                                <Icon sx={{ rotate: expandActiveFecha ? "180deg" : "0deg", transition: "0.3s" }}>expand_more</Icon>
                                            </SoftButton>

                                            {fechaOption === "personalizado" && (
                                                <SoftBox display="flex" gap={2}>
                                                    <SoftInput
                                                        type="date"
                                                        value={filters.fechaDesde || ""}
                                                        onChange={(e) => handleFilterChange("fechaDesde", e.target.value)}
                                                        sx={{ borderRadius: "10px" }}
                                                    />
                                                    <SoftInput
                                                        type="date"
                                                        value={filters.fechaHasta || ""}
                                                        onChange={(e) => handleFilterChange("fechaHasta", e.target.value)}
                                                        sx={{ borderRadius: "10px" }}
                                                    />
                                                </SoftBox>
                                            )}
                                        </SoftBox>
                                    </SoftBox>
                                </Card>
                            </Grid>

                            {/* Card 3: Búsqueda Específica */}
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%", border: "1px solid", borderColor: "grey.200", boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)" }}>
                                    <SoftBox p={2.5} display="flex" flexDirection="column" height="100%">
                                        <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
                                            <Icon color="info" fontSize="small">search</Icon>
                                            <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase">
                                                Búsqueda
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox display="flex" flexDirection="column" gap={1.5}>
                                            <SoftInput
                                                placeholder="N° Remito o Provisión"
                                                value={filters.idRemito || filters.idProvision || ""}
                                                onChange={(e) => handleSearchChange(e.target.value)}
                                                icon={{ component: "search", direction: "left" }}
                                                sx={{ borderRadius: "10px" }}
                                            />
                                            <SoftBox mt="auto" pt={1} display="flex" justifyContent="flex-end">
                                                <SoftButton
                                                    variant="text"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => {
                                                        setFilters({ proveedor: "", estado: [...ALL_ESTADOS], fechaDesde: "", fechaHasta: "", idRemito: "", idProvision: "", tipoFecha: "" });
                                                        setCurrentPage(1);
                                                        setFechaOption(null);
                                                    }}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    <Icon sx={{ mr: 0.5 }}>cleaning_services</Icon>
                                                    Limpiar filtros
                                                </SoftButton>
                                            </SoftBox>
                                        </SoftBox>
                                    </SoftBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </SoftBox>
                </Collapse>

                {/* Main Content Area */}
                <Card sx={{ border: "none", boxShadow: "0 20px 27px 0 rgba(0,0,0,0.05)", borderRadius: "16px", overflow: "visible" }}>
                    <SoftBox p={3}>
                        {loadingProvisiones ? (
                            <SoftBox display="flex" justifyContent="center" alignItems="center" py={10}>
                                <CircularProgress color="info" />
                            </SoftBox>
                        ) : (
                            <>
                                <DataTableCustom
                                    columns={remitosColumns}
                                    data={remitos}
                                    loading={false}
                                    loadingActions={loadingFactura}
                                    showActions={false}
                                    maxHeight="550px"
                                    showFooter={true}
                                    zebraStripes={false}
                                    hover={true}
                                    border={false}
                                    emptyMessage="No se encontraron remitos con los criterios seleccionados"
                                    sx={{
                                        "& .MuiDataGrid-row:hover": {
                                            backgroundColor: "rgba(248, 249, 250, 1) !important",
                                            transition: "0.2s"
                                        },
                                        "& .MuiDataGrid-columnHeader": {
                                            backgroundColor: "#f8f9fa"
                                        }
                                    }}
                                    footerContent={
                                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%" px={2}>
                                            <SoftBox display="flex" gap={3}>
                                                <SoftBox>
                                                    <SoftTypography variant="button" color="text" fontWeight="bold">
                                                        Total Remitos:
                                                    </SoftTypography>
                                                    <SoftTypography variant="button" color="dark" fontWeight="bold" sx={{ ml: 1 }}>
                                                        {pagesInfo?.total ?? remitos?.length ?? 0}
                                                    </SoftTypography>
                                                </SoftBox>
                                                {filters.estado?.length > 0 && (
                                                    <SoftBox>
                                                        <SoftTypography variant="button" color="text" fontWeight="bold">
                                                            Estados:
                                                        </SoftTypography>
                                                        <SoftBadge
                                                            variant="gradient"
                                                            color="info"
                                                            badgeContent={filters.estado.length}
                                                            size="extra-small"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    </SoftBox>
                                                )}
                                            </SoftBox>
                                            <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
                                                Página {currentPage} de {totalPages}
                                            </SoftTypography>
                                        </SoftBox>
                                    }
                                />
                                {remitos.length > 0 && (
                                    <SoftBox mt={2} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                        <SoftButton
                                            size="small"
                                            variant="outlined"
                                            color="dark"
                                            disabled={currentPage <= 1}
                                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                        >
                                            Anterior
                                        </SoftButton>
                                        <SoftTypography variant="button" color="text" fontWeight="medium">
                                            {currentPage} / {totalPages}
                                        </SoftTypography>
                                        <SoftButton
                                            size="small"
                                            variant="outlined"
                                            color="dark"
                                            disabled={currentPage >= totalPages}
                                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                        >
                                            Siguiente
                                        </SoftButton>
                                    </SoftBox>
                                )}
                            </>
                        )}
                    </SoftBox>
                </Card>

                {renderMenu}
            </SoftBox>

            <ModalOrdenPago
                open={ordenPagoOpen}
                idRemito={selectedRemito?.id ?? null}
                infoPago={selectedRemito ?? null}
                handleClose={() => {
                    setOrdenPagoOpen(false);
                    setSelectedRemito(null);
                }}
                onSuccess={refetchProvisiones}
            />
            <DinamicModal
                open={openConfirmar}
                handleClose={() => {
                    setOpenConfirmar(false);
                    setSelectedRemito(null);
                }}
                title="Motivo del rechazo de la factura"
                data={{
                    Mensaje: selectedRemito?.pagosProveedor?.motivoRechazo || "No se proporcionó un motivo.",
                }}
            />
            <CalendarioPagos open={openCalendario} handleClose={() => setOpenCalendario(false)} />
            <Footer />
        </DashboardLayout>
    )
}

export default Pagos








